/**
 * The bridge release page store (AON-11 §6): one bridge request at a time, read and resolved from the owner's own
 * session, where the always-on space is mounted.
 *
 * - Every request goes through `sendRequest`, which correlates the reply by the envelope's request id. State is
 *   keyed by the BRIDGE request id (`requests`, `drafts`, `resolving`), so a reply for request A can never write the
 *   view of request B. There is no single-slot flag.
 * - Each call has a timeout (`sendRequest`'s `timeoutMs`); a timeout rejects, and a rejection is the path to
 *   `'error'`. Nothing waits forever.
 * - The Private search covers documents AND notes through the existing WS paths (`SearchDocumentsRequest` and the
 *   notes' `SearchNoteEntitiesRequest`, which matches note titles). It is latest-wins: a newer search aborts the older
 *   requests (AbortController) and a sequence number drops any reply that still lands. The content cache is keyed by
 *   source kind and id, so a stale read writes only its own entry.
 * - `load` keeps the cached view while it refetches, so the page does not flash a spinner on a revisit.
 * - The store is not persisted: everything in it is one user's protected content. `resetBridgeStore` drops it.
 */
import { create as createMessage } from '@bufbuild/protobuf';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  BridgeDraftRequestSchema,
  BridgeRequestGetRequestSchema,
  BridgeResolveRequestSchema,
  BridgeSetSendApprovalRequestSchema,
  type BridgeRequestGetResponse,
} from '@/lib/protobuf/gen/bridge/bridge_pb';
import { GetDocumentContentRequestSchema, SearchDocumentsRequestSchema } from '@/lib/protobuf/gen/document/document_pb';
import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';
import { GetNoteRequestSchema, SearchNoteEntitiesRequestSchema } from '@/lib/protobuf/gen/note/note_pb';
import { RequestError, sendRequest } from '@/lib/websocket';

/** A read of one request, a draft, a search or a document read. */
export const BRIDGE_REQUEST_TIMEOUT_MS = 30_000;
/**
 * A resolve re-arms the run and sends the reply before it answers. Like the Needs-you resolve, the client waits a
 * little longer than the server's 2-minute handler deadline.
 */
export const BRIDGE_RESOLVE_TIMEOUT_MS = 125_000;

export type BridgeAction = 'release' | 'answer' | 'decline';

/**
 * Why a call failed, in the terms the page shows.
 * - `notFound`: no such request for this user.
 * - `locked`: the space is not mounted in this session (423) — "Unlock to answer".
 * - `unavailable`: the server cannot answer right now (503); retry shortly. The space may be open on another server,
 *   the request may be being answered, or a dependency is down: all three share one code, so the page names none.
 * - `conflict`: the request is already being handled, or was handled (409).
 * - `error`: anything else, a timeout included. Nothing is known to have happened.
 */
export type BridgeErrorKind = 'notFound' | 'locked' | 'unavailable' | 'conflict' | 'error';

/** Plain view of a `BridgeRequestGetResponse` (a proto message must not go through immer). */
export interface BridgeRequestView {
  id: string;
  need: string;
  hint: Record<string, string>;
  excerpt: string;
  channel: string;
  requester: string;
  createdAt: string;
  expiresAt: string;
  /** open | resolving | expiring | released | answered | declined | expired | closed */
  state: string;
  holdingSent: boolean;
  declineText: string;
}

export interface BridgeRequestSlot {
  status: 'loading' | 'ready' | 'error';
  /** The last good read. Kept while a refetch runs and when it fails. */
  view?: BridgeRequestView;
  error?: BridgeErrorKind;
}

export interface BridgeDraftSlot {
  status: 'loading' | 'ready' | 'error';
  reply?: string;
}

export interface BridgeResolveOutcome {
  ok: boolean;
  error?: BridgeErrorKind;
}

export type BridgeSourceKind = 'document' | 'note';

export interface BridgeSearchHit {
  kind: BridgeSourceKind;
  id: string;
  title: string;
  /** '' for a note: the note search returns titles only. */
  excerpt: string;
}

export interface BridgeSearchState {
  query: string;
  status: 'idle' | 'loading' | 'ready' | 'error';
  results: BridgeSearchHit[];
  /** One of the two searches failed; `results` holds the other one's hits. */
  partial?: boolean;
  error?: BridgeErrorKind;
}

export interface BridgeContentSlot {
  status: 'loading' | 'ready' | 'error';
  text?: string;
  error?: BridgeErrorKind;
}

/** What a release pins: exactly these bytes are stored and sent. */
export interface BridgeSnippet {
  sourceKind: BridgeSourceKind;
  sourceId: string;
  /** `selection` (a passage the owner selected) or `content` (the whole item). */
  field: string;
  text: string;
}

export interface BridgeStoreState {
  /** The user the cached content belongs to; '' before the first bind. */
  boundUser: string;
  requests: Record<string, BridgeRequestSlot>;
  drafts: Record<string, BridgeDraftSlot>;
  /** bridge request id → the action in flight for it. */
  resolving: Record<string, BridgeAction>;
  /** bridge request id → the outcome of its last resolve. */
  outcomes: Record<string, BridgeResolveOutcome>;
  search: BridgeSearchState;
  /** `contentKey(kind, id)` → the item's text. */
  content: Record<string, BridgeContentSlot>;
  actions: {
    /**
     * Binds the cache to the signed-in user. A cache filled for ANOTHER user is dropped first: everything here is
     * one user's protected content, and a soft logout does not unload the page.
     */
    bindUser(userId: string): void;
    /** Reads one request. Keeps a cached view while loading. Never rejects. */
    load(id: string): Promise<void>;
    /** Asks the assistant for a reply. The draft is text only; nothing is sent. Never rejects. */
    draft(id: string, snippet: string, instructions?: string): Promise<string | null>;
    /**
     * Resolves the request with exactly `reply` (and `snippet` for a release), then reloads it. A second call for a
     * request already in flight is dropped. Never rejects.
     */
    resolve(id: string, action: BridgeAction, pinned: { reply: string; snippet?: BridgeSnippet }): Promise<BridgeResolveOutcome>;
    /** Latest-wins search over the owner's Private documents and notes. An empty query clears. Never rejects. */
    search(query: string): Promise<void>;
    /** Reads one item's text into `content[contentKey(kind, id)]`. Never rejects. */
    loadContent(kind: BridgeSourceKind, id: string): Promise<void>;
  };
}

/**
 * Classify a rejection from `sendRequest` by its wire code only, never by its message text: the server's messages
 * are copy, and a text match guards one spelling.
 */
export function classifyBridgeError(err: unknown): BridgeErrorKind {
  if (!(err instanceof RequestError)) return 'error';
  const { code, codeName } = err.wire;
  if (code === ErrorCode.NOT_FOUND || codeName === 'ERROR_CODE_NOT_FOUND') return 'notFound';
  // 423 maps to the generic *_SESSION_LOCKED code on the wire (domain.CodeFromStatus).
  if (codeName?.endsWith('_SESSION_LOCKED') || code === ErrorCode.RECORDING_SESSION_LOCKED) return 'locked';
  // One 503 code covers "open on another server", "being answered right now" and a missing dependency.
  if (code === ErrorCode.UNAVAILABLE || codeName === 'ERROR_CODE_UNAVAILABLE') return 'unavailable';
  if (code === ErrorCode.CONFLICT || codeName === 'ERROR_CODE_CONFLICT') return 'conflict';
  return 'error';
}

/** The `content` cache key of one search hit. */
export function contentKey(kind: BridgeSourceKind, id: string): string {
  return `${kind}:${id}`;
}

/**
 * The text the page shows and slices, with every line ending as LF. A textarea's value is LF-normalised, so its
 * selection offsets index the normalised text; slicing CRLF text with them would pin a shifted passage.
 */
export function normaliseLineEndings(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

export function toBridgeView(msg: BridgeRequestGetResponse): BridgeRequestView {
  return {
    id: msg.id,
    need: msg.need,
    hint: { ...msg.hint },
    excerpt: msg.excerpt,
    channel: msg.channel,
    requester: msg.requester,
    createdAt: msg.createdAt,
    expiresAt: msg.expiresAt,
    state: msg.state,
    holdingSent: msg.holdingSent,
    declineText: msg.declineText,
  };
}

/** Bumped by `resetBridgeStore`; a call that started before a reset writes nothing after it. */
let generation = 0;
/** Latest-wins token per bridge request id for `load`. */
const loadSeq = new Map<string, number>();
let searchSeq = 0;
let searchRequest: AbortController | null = null;
/** Latest-wins token per content key for `loadContent`. */
const contentSeq = new Map<string, number>();

function bump(map: Map<string, number>, key: string): number {
  const next = (map.get(key) ?? 0) + 1;
  map.set(key, next);
  return next;
}

export const useBridgeStore = create<BridgeStoreState>()(
  devtools(
    immer((set, get) => ({
      boundUser: '',
      requests: {},
      drafts: {},
      resolving: {},
      outcomes: {},
      search: { query: '', status: 'idle', results: [] },
      content: {},
      actions: {
        bindUser: (userId) => {
          const bound = get().boundUser;
          if (bound === userId) return;
          if (bound !== '') resetBridgeStore();
          set((d) => {
            d.boundUser = userId;
          });
        },

        load: async (id) => {
          const gen = generation;
          const seq = bump(loadSeq, id);
          set((d) => {
            const prev = d.requests[id];
            d.requests[id] = prev?.view ? { status: 'loading', view: prev.view } : { status: 'loading' };
          });
          try {
            const resp = await sendRequest(
              { case: 'bridgeRequestGetRequest', value: createMessage(BridgeRequestGetRequestSchema, { id }) },
              'bridgeRequestGetResponse',
              { timeoutMs: BRIDGE_REQUEST_TIMEOUT_MS },
            );
            if (gen !== generation || loadSeq.get(id) !== seq) return;
            const view = toBridgeView(resp);
            set((d) => {
              d.requests[id] = { status: 'ready', view };
            });
          } catch (err) {
            if (gen !== generation || loadSeq.get(id) !== seq) return;
            const kind = classifyBridgeError(err);
            set((d) => {
              const prev = d.requests[id];
              d.requests[id] = prev?.view
                ? { status: 'error', view: prev.view, error: kind }
                : { status: 'error', error: kind };
            });
          }
        },

        draft: async (id, snippet, instructions = '') => {
          const gen = generation;
          set((d) => {
            d.drafts[id] = { status: 'loading' };
          });
          try {
            const resp = await sendRequest(
              {
                case: 'bridgeDraftRequest',
                value: createMessage(BridgeDraftRequestSchema, { id, snippet, instructions }),
              },
              'bridgeDraftResponse',
              { timeoutMs: BRIDGE_REQUEST_TIMEOUT_MS },
            );
            if (gen !== generation) return null;
            const reply = resp.reply;
            set((d) => {
              d.drafts[id] = { status: 'ready', reply };
            });
            return reply;
          } catch {
            if (gen !== generation) return null;
            // The page shows one draft error whatever the cause: the owner writes the reply instead.
            set((d) => {
              d.drafts[id] = { status: 'error' };
            });
            return null;
          }
        },

        resolve: async (id, action, pinned) => {
          if (get().resolving[id] !== undefined) return { ok: false, error: 'conflict' };
          const gen = generation;
          set((d) => {
            d.resolving[id] = action;
            delete d.outcomes[id];
          });
          const snippet = action === 'release' ? pinned.snippet : undefined;
          let outcome: BridgeResolveOutcome;
          try {
            await sendRequest(
              {
                case: 'bridgeResolveRequest',
                value: createMessage(BridgeResolveRequestSchema, {
                  id,
                  action,
                  snippetSourceKind: snippet?.sourceKind ?? '',
                  snippetSourceId: snippet?.sourceId ?? '',
                  snippetField: snippet?.field ?? '',
                  snippetText: snippet?.text ?? '',
                  // Decline sends no reply text: the server uses the configured decline text, or none.
                  reply: action === 'decline' ? '' : pinned.reply,
                }),
              },
              'bridgeResolveResponse',
              { timeoutMs: BRIDGE_RESOLVE_TIMEOUT_MS },
            );
            // The reload below reads the new state; the response's state is not needed.
            outcome = { ok: true };
          } catch (err) {
            outcome = { ok: false, error: classifyBridgeError(err) };
          }
          if (gen !== generation) return outcome;
          set((d) => {
            delete d.resolving[id];
            d.outcomes[id] = outcome;
          });
          // Reload either way: a timeout does not prove the resolve failed, and a conflict means the state moved.
          await get().actions.load(id);
          return outcome;
        },

        search: async (query) => {
          const gen = generation;
          const seq = ++searchSeq;
          searchRequest?.abort();
          searchRequest = null;
          const q = query.trim();
          if (!q) {
            set((d) => {
              d.search = { query: '', status: 'idle', results: [] };
            });
            return;
          }
          const request = new AbortController();
          searchRequest = request;
          set((d) => {
            d.search = { query: q, status: 'loading', results: d.search.query === q ? d.search.results : [] };
          });
          const opts = { timeoutMs: BRIDGE_REQUEST_TIMEOUT_MS, signal: request.signal };
          const [docs, notes] = await Promise.allSettled([
            sendRequest(
              {
                case: 'searchDocumentsRequest',
                value: createMessage(SearchDocumentsRequestSchema, { collectionId: '', query: q, limit: 20 }),
              },
              'searchResultsResponse',
              opts,
            ),
            sendRequest(
              { case: 'searchNoteEntitiesRequest', value: createMessage(SearchNoteEntitiesRequestSchema, { query: q }) },
              'searchNoteEntitiesResponse',
              opts,
            ),
          ]);
          if (searchRequest === request) searchRequest = null;
          if (gen !== generation || seq !== searchSeq) return;
          if (docs.status === 'rejected' && notes.status === 'rejected') {
            const kind = classifyBridgeError(docs.reason);
            set((d) => {
              d.search = { query: q, status: 'error', results: [], error: kind };
            });
            return;
          }
          const results: BridgeSearchHit[] = [];
          if (docs.status === 'fulfilled') {
            for (const r of docs.value.results) {
              results.push({ kind: 'document', id: r.documentId, title: r.title, excerpt: r.excerpt });
            }
          }
          if (notes.status === 'fulfilled') {
            // The entity search also returns research and documents; only notes are a source the page reads.
            for (const e of notes.value.entities) {
              if (e.entityType === 'note') results.push({ kind: 'note', id: e.entityId, title: e.title, excerpt: '' });
            }
          }
          const partial = docs.status === 'rejected' || notes.status === 'rejected';
          set((d) => {
            d.search = partial ? { query: q, status: 'ready', results, partial } : { query: q, status: 'ready', results };
          });
        },

        loadContent: async (kind, id) => {
          const key = contentKey(kind, id);
          const gen = generation;
          const seq = bump(contentSeq, key);
          if (get().content[key]?.status === 'ready') return;
          set((d) => {
            d.content[key] = { status: 'loading' };
          });
          try {
            let raw: string;
            if (kind === 'note') {
              const resp = await sendRequest(
                { case: 'getNoteRequest', value: createMessage(GetNoteRequestSchema, { noteId: id }) },
                'noteResponse',
                { timeoutMs: BRIDGE_REQUEST_TIMEOUT_MS },
              );
              raw = resp.note?.content ?? '';
            } else {
              const resp = await sendRequest(
                {
                  case: 'getDocumentContentRequest',
                  value: createMessage(GetDocumentContentRequestSchema, { documentId: id }),
                },
                'documentContentResponse',
                { timeoutMs: BRIDGE_REQUEST_TIMEOUT_MS },
              );
              // The converted markdown is the readable text; raw bytes are the fallback for a plain-text document.
              raw = resp.markdown || new TextDecoder().decode(resp.content);
            }
            if (gen !== generation || contentSeq.get(key) !== seq) return;
            const text = normaliseLineEndings(raw);
            set((d) => {
              d.content[key] = { status: 'ready', text };
            });
          } catch (err) {
            if (gen !== generation || contentSeq.get(key) !== seq) return;
            const errKind = classifyBridgeError(err);
            set((d) => {
              d.content[key] = { status: 'error', error: errKind };
            });
          }
        },
      },
    })),
    { name: 'bridge-request' },
  ),
);

/**
 * Arms (allow=true) or disarms send-gate key 1 on one bridge step of the LIVE spec (AON-11 CARRIED-1). A dedicated
 * request, never the workflow save body: a deployed workflow's save goes to its draft, and the save path must not
 * be the writer of key 1. Resolves with the armed state the server now stores; rejects on any failure.
 */
export async function setBridgeSendApproval(workflowId: string, stepId: string, allow: boolean): Promise<boolean> {
  const resp = await sendRequest(
    {
      case: 'bridgeSetSendApprovalRequest',
      value: createMessage(BridgeSetSendApprovalRequestSchema, { workflowId, stepId, allow }),
    },
    'bridgeSetSendApprovalResponse',
    { timeoutMs: BRIDGE_REQUEST_TIMEOUT_MS },
  );
  return resp.allow;
}

/** Drop every request's content and every call in flight (sign-out, user switch, tests). */
export function resetBridgeStore(): void {
  generation++;
  loadSeq.clear();
  contentSeq.clear();
  searchSeq++;
  searchRequest?.abort();
  searchRequest = null;
  useBridgeStore.setState((s) => ({
    ...s,
    boundUser: '',
    requests: {},
    drafts: {},
    resolving: {},
    outcomes: {},
    search: { query: '', status: 'idle', results: [] },
    content: {},
  }));
}
